import classNames from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import type { Props as HighlightExtraElementWrapperProps } from '#src/components/onboarding/HighlightExtraElementWrapper/index.tsx';
import { HighlightExtraElementWrapper } from '#src/components/onboarding/HighlightExtraElementWrapper/index.tsx';
import { OnboardingButton } from '#src/components/onboarding/OnboardingButton/index.tsx';
import { glossary } from '#src/lib/glossary.ts';
import { tabletAndAbove, tabletAndBelow } from '#src/style/mediaQuery.ts';

const AnchoredContent = styled(HighlightExtraElementWrapper)<{
  $pinToBottomOnMobile?: boolean;
}>`
  align-items: flex-end;

  ${({ $pinToBottomOnMobile }) =>
    $pinToBottomOnMobile &&
    css`
      display: none;
      @media ${tabletAndAbove} {
        display: block;
      }
    `}
`;

const BlurredMessage = styled.div`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-100-opaque);
  backdrop-filter: blur(10px);
  text-align: center;
`;

const InlineButton = styled(OnboardingButton)`
  display: none;

  @media ${tabletAndBelow} {
    &.showOnMobile {
      display: block;
    }
  }

  @media ${tabletAndAbove} {
    display: block;
  }
`;

const MobileFooter = styled(Vertical).attrs({ gap: 1 })`
  position: absolute;
  bottom: var(--double-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  @media ${tabletAndAbove} {
    display: none;
  }
`;

type Props = {
  children: ReactNode;
  buttonProps?: Partial<ComponentProps<typeof OnboardingButton>>;
  mobileButtonPlacement?: 'pinnedToBottom' | 'belowMessage';
  pinToBottomOnMobile?: boolean;
};

export const ExtraMessageWithBlurredBg = ({
  children,
  className,
  buttonProps,
  mobileButtonPlacement = 'pinnedToBottom',
  pinToBottomOnMobile,
  ...rest
}: Props & HighlightExtraElementWrapperProps) => {
  const { ready = true } = rest?.positionProps || {};

  if (!ready) {
    return null;
  }

  const buttonLabel = buttonProps?.children ?? (
    <FormattedMessage {...glossary.continue} />
  );
  const showPinnedMobileButton =
    !!buttonProps && mobileButtonPlacement === 'pinnedToBottom';

  return (
    <>
      <AnchoredContent
        {...rest}
        className={className}
        $pinToBottomOnMobile={pinToBottomOnMobile}
      >
        <Vertical gap={1} center>
          <BlurredMessage>{children}</BlurredMessage>
          {!!buttonProps && (
            <InlineButton
              size="medium"
              color="secondary"
              {...buttonProps}
              className={classNames(buttonProps.className, {
                showOnMobile: mobileButtonPlacement === 'belowMessage',
              })}
            >
              {buttonLabel}
            </InlineButton>
          )}
        </Vertical>
      </AnchoredContent>
      {(!!pinToBottomOnMobile || !!showPinnedMobileButton) && (
        <MobileFooter>
          {!!pinToBottomOnMobile && <BlurredMessage>{children}</BlurredMessage>}
          {!!showPinnedMobileButton && (
            <Button fullWidth size="large" color="secondary" {...buttonProps}>
              {buttonLabel}
            </Button>
          )}
        </MobileFooter>
      )}
    </>
  );
};
